import React, { useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { ListingOtQuery } from '../../../../graphql-types';
import { CardOt } from './cardOt/CardOt';
import { Toolbar } from './toolbar/Toolbar';
import { Button } from '../button/Button';
import { Path, usePageContext } from '../../../hooks/contexts/usePageContext';
import { Modal } from '../modal/Modal';
import { FilterMenuDesktop } from './filterMenuDesktop/FilterMenuDesktop';
import { DeviceEnum } from '../../../utils/getDevice';
import { initMinMaxCriterias } from '../../../utils/initMinMaxCriterias';
import { Filters, MinMax, SanityFilters } from '../../../utils/types';
import * as styles from './listingOt.module.scss';
import { useWindowSize } from '../../../hooks/useWindowSize';

const OFFSET_DESKTOP = 8;
const OFFSET_TAB_MOB = 4;

interface IListingOtProps {
  otCodes: (string | null | undefined)[] | undefined;
  sanityFilters: SanityFilters;
  rubriquePath?: Path;
}

const fitlerOtsByCodes = (
  allIngenieOt: ListingOtQuery['allIngenieOt'],
  otCodes: (string | null | undefined)[] | undefined
) =>
  allIngenieOt?.nodes.filter((ot) =>
    ot.selectionsCodes?.some((code) => otCodes?.includes(code ?? ''))
  );

export const ListingOt = ({ otCodes, sanityFilters, rubriquePath }: IListingOtProps) => {
  const { allIngenieOt, sanityTranslations } = useStaticQuery<ListingOtQuery>(QUERY);

  const { device } = useWindowSize();
  const [open, setOpenModal] = useState(false);
  const { lang } = usePageContext();
  const [page, setPage] = useState(1);
  const ots = fitlerOtsByCodes(allIngenieOt, otCodes);
  const [filteredOts, setFilteredOts] = useState<ListingOtQuery['allIngenieOt']['nodes']>(ots);
  const minMaxCriterias = initMinMaxCriterias(ots);

  /**
   * Check if criteria value is included between filter's value
   * @param filterValue
   * @param criteriaValue
   */
  const isInRange = (filterValue: boolean | MinMax, criteriaValue: number | MinMax) => {
    if (!criteriaValue) return true;
    if (typeof filterValue !== 'boolean') {
      if (typeof criteriaValue === 'number') {
        return criteriaValue >= filterValue.min && criteriaValue <= filterValue.max;
      }
      return criteriaValue.max >= filterValue.min && criteriaValue.max <= filterValue.max;
    }
    return false;
  };

  const handleFiltering = (ot: ListingOtQuery['allIngenieOt']['nodes'][0], filters: Filters) => {
    const booleanFilters = filters?.filter(
      (filter) => filter?.criteria && filter.type === 'checkbox'
    );
    const allBooleanFiltersInactive = booleanFilters?.every((f) => f.active === false);
    const matchBooleanFilters = allBooleanFiltersInactive
      ? true
      : booleanFilters?.some((filter) => filter?.criteria && ot?.criteres210?.[filter?.criteria]);
    const nonBooleanFilters = filters?.filter(
      (filter) => filter?.criteria && typeof filter.filterValue !== 'boolean'
    );
    const matchNonBooleanFilters = nonBooleanFilters?.every(
      (f) => f?.criteria && isInRange(f.filterValue, ot?.criteres210?.[f?.criteria])
    );
    return matchBooleanFilters && matchNonBooleanFilters;
  };

  const onChangeFilters = (filters: Filters) => {
    const tmpOts = ots.filter((ot) => handleFiltering(ot, filters));
    setFilteredOts(tmpOts);
  };

  filteredOts.sort((a, b) => {
    if (a.reservable === b.reservable) {
      return 0;
    }
    return a.reservable ? -1 : 1;
  });

  return (
    <>
      <div className={styles.wrapper}>
        <Toolbar
          setOpenModal={setOpenModal}
          nbResults={filteredOts.length}
          filtersIsProvided={!!sanityFilters}
        />
        <div className={styles.contentWrapper}>
          {sanityFilters ? (
            <div className={styles.stickyWrapper}>
              <div className={styles.stickyFilters}>
                <FilterMenuDesktop
                  onChangeFilters={onChangeFilters}
                  sanityFilters={sanityFilters}
                  minMaxCriterias={minMaxCriterias}
                />
              </div>
            </div>
          ) : null}
          <ul className={styles.listing} data-filters={!!sanityFilters}>
            {filteredOts
              .slice(0, page * (device === DeviceEnum.desktop ? OFFSET_DESKTOP : OFFSET_TAB_MOB))
              .map((ot) => (
                <li key={ot?.id}>
                  <CardOt ot={ot} rubriquePath={rubriquePath} />
                </li>
              ))}
          </ul>
        </div>
        {page * (device === DeviceEnum.desktop ? OFFSET_DESKTOP : OFFSET_TAB_MOB) <
        filteredOts?.length ? (
          <div className={styles.buttonWrapper} data-filters={!!sanityFilters}>
            <Button onClick={() => setPage(page + 1)} name="more">
              {sanityTranslations?._rawProductPaginationButton?.[lang]}
            </Button>
          </div>
        ) : null}
      </div>
      <Modal
        fullScreenMobile
        open={open}
        setOpenModal={setOpenModal}
        translations={sanityTranslations}
      >
        {sanityFilters ? (
          <FilterMenuDesktop
            onValidateFilters={() => setOpenModal(false)}
            onChangeFilters={(filters) => sanityFilters && onChangeFilters(filters)}
            sanityFilters={sanityFilters}
            minMaxCriterias={minMaxCriterias}
          />
        ) : null}
      </Modal>
    </>
  );
};

const QUERY = graphql`
  query ListingOt {
    allIngenieOt {
      nodes {
        ...cardOtFragment
        ...wishListFragment
        pictosPartners {
          url
          alt
        }
        selections {
          id
        }
        medias {
          url
        }
        displayableContact {
          address
        }
        selectionsCodes
        criteres210 {
          accesPMR
          accroBranche
          animauxAcceptes
          aPied
          appartement
          apresMidi
          art
          baladeAccompagnee
          baladeDegustation
          bateauPromenade
          bowling
          canoeKayakPaddle
          capacite
          caveCaviste
          chateauxDemeuresHistoriques
          classement
          classementBis
          cuisineBio
          cuisineGastronomique
          cuisineMditerraneene
          cuisineMonde
          cuisineTraditionnelle
          cuisineVegan
          cuisineVgetarienne
          dansLesAirs
          degustations
          enVelo
          equitation
          escapeGame
          etoile
          gastronomieEtVins
          golf
          guinguette
          histoirePatrimoine
          itineraireVelo
          jeunePublic
          inclusDansToursCityPass
          lesPlusVisites
          moinsDe25KmDeTours
          de25KmA45KmDeTours
          plusDe45KmDeTours
          distanceFromToursOffice
          karting
          laserGame
          veloFriendly
          location
          maison
          matin
          multiSport
          musique
          nature
          parcsLoisirs
          parcsZoos
          patinoire
          piscine
          piscineJeuAquatique
          piscineBienEtre
          plusDeRdv
          prix {
            max
            min
          }
          sejour2j1n
          sejour3j2n
          sejour4j3n
          sejour5jmin
          sevenInattendues
          soiree
          sport
          sportMecaniques
          tablehote
          toutInclus
          trainsTouristiques
          transportBillets
          transportBilletsDejeuner
          transportBilletsVisiteGuidees
          transportUniquement
          visitesGuidees
        }
      }
    }
    sanityTranslations {
      _rawToggleFilter
      _rawToggleFilter2
      _rawProductPaginationButton
      _rawModalFilterReturn
      _rawTitleServices
    }
  }
`;
